import moment from "moment-timezone";

function toResourceId(value: any): string | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") {
    const nested = value._id ?? value.id;
    if (nested != null && nested !== "") return String(nested);
    return undefined;
  }
  const s = String(value).trim();
  return !s || s === "[object Object]" ? undefined : s;
}

function overlapResourceKey(event: any): string {
  return (
    toResourceId(event?.resourceId) ||
    toResourceId(event?.data?.appointment?.doctorId) ||
    "unassigned"
  );
}

function isOffCalendarAppointment(event: any): boolean {
  const appt = event?.data?.appointment;
  if (!appt) return false;
  const tags = [
    appt.pre_appointment_status,
    appt.preAppointmentStatus,
    appt.booking_source,
    appt.bookingSource,
    appt.status,
  ]
    .map((v) => String(v ?? "").trim().toLowerCase())
    .join(" ");
  return (
    tags.includes("walk_in") ||
    tags.includes("walkin") ||
    tags.includes("call_in") ||
    tags.includes("callin")
  );
}

function isInactiveForSlotOverlap(event: any): boolean {
  if (event?.data?.blockout) return true;
  const apt = event?.data?.appointment;
  if (!apt) return true;
  const pas = String(
    apt.pre_appointment_status ?? apt.preAppointmentStatus ?? ""
  )
    .trim()
    .toLowerCase();
  if (pas === "cancel" || pas === "cancelled" || pas === "canceled") {
    return true;
  }
  const st = String(apt.status ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  return (
    st === "cancelled" ||
    st === "canceled" ||
    st === "none" ||
    st === "cancel" ||
    st === "tray"
  );
}

/** Touching end===start is allowed; overlapping ranges are not. */
export function dateRangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

export function findOverlappingAppointmentEvent(
  events: any[] | undefined,
  params: {
    start: Date;
    end: Date;
    doctorId?: unknown;
    excludeAppointmentId?: string;
  }
): any | null {
  if (!events?.length || !params.start || !params.end) return null;
  const doctorKey = toResourceId(params.doctorId);
  const exclude = String(params.excludeAppointmentId ?? "").trim();

  for (const event of events) {
    if (!event?.data?.appointment) continue;
    if (isOffCalendarAppointment(event)) continue;
    if (isInactiveForSlotOverlap(event)) continue;

    const evId = String(
      event?.data?.appointment?.id ?? event?.data?.appointment?._id ?? ""
    ).trim();
    if (exclude && evId && evId === exclude) continue;

    const evDoctor = overlapResourceKey(event);
    if (doctorKey && evDoctor !== "unassigned" && evDoctor !== doctorKey) continue;

    const evStart = event.start instanceof Date ? event.start : new Date(event.start);
    const evEnd = event.end instanceof Date ? event.end : new Date(event.end);
    if (dateRangesOverlap(params.start, params.end, evStart, evEnd)) {
      return event;
    }
  }
  return null;
}

export function occupiedSlotMessage(event: any): string {
  const start = moment(event?.start).isValid()
    ? moment(event.start).format("HH:mm")
    : "";
  const end = moment(event?.end).isValid()
    ? moment(event.end).format("HH:mm")
    : "";
  if (start && end) {
    return `This time overlaps another appointment (${start}–${end}). Choose a free slot.`;
  }
  return "This time slot is already booked. Choose a free slot.";
}
